import React, { useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import "./Contact.css";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiSend,
  FiMessageCircle,
} from "react-icons/fi";

export default function Contact() {
  const [status, setStatus] = useState("idle"); // idle | sent

  const info = useMemo(
    () => [
      {
        icon: <FiMail />,
        title: "Email",
        value: "info@Ayrine.in",
        sub: "We reply within 24 hours.",
      },
      {
        icon: <FiPhone />,
        title: "Phone",
        value: "(+91) 9885881181",
        sub: "Mon–Sat, 10am–7pm.",
      },
      {
        icon: <FiMapPin />,
        title: "Address",
        value: "Hyderabad, India",
        sub: "Shipping across India.",
      },
      {
        icon: <FiClock />,
        title: "Support Hours",
        value: "Monday – Saturday: 10:00 AM – 10:00 PM",
        sub: "Sunday: Closed",
      },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        q: "How long does delivery take?",
        a: "Most orders are delivered within 3–7 business days depending on your location.",
      },
      {
        q: "Do you accept returns?",
        a: "Yes. If there’s any issue, contact us within 48 hours of delivery and we’ll help you.",
      },
      {
        q: "Can I change my address after ordering?",
        a: "If the order hasn’t shipped, we can update it. Message us quickly with your order ID.",
      },
    ],
    []
  );

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    orderId: "",
    topic: "order",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { error } = await supabase.from('contact_messages').insert([{
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        order_id: formData.orderId,
        topic: formData.topic,
        message: formData.message
      }]);

      if (error) throw error;
      
      setStatus("sent");
      
      // Clear form after submission
      setFormData({
        name: "",
        email: "",
        mobile: "",
        orderId: "",
        topic: "order",
        message: ""
      });

      // Keep the toast message for 5 seconds
      setTimeout(() => setStatus("idle"), 5000);
    } catch (err) {
      console.error("Error saving message", err);
      alert("Failed to send message. Please try again.");
    }
  };

  return (
    <div className="contactPage">
      {/* HERO */}
      <section className="cHero">
        <div className="cHeroInner">
          <p className="cKicker">Ayrine Support</p>
          <h1 className="cTitle">Contact Us</h1>
          <p className="cSub">
            Questions about an order, sizing, or styling? We’re here to help.
          </p>

          <div className="cQuick">
            <div className="cQuickItem">
              <span className="cQuickIcon">
                <FiMessageCircle />
              </span>
              <div>
                <div className="cQuickTop">Fast Replies</div>
                <div className="cQuickBottom">Usually within 24 hours</div>
              </div>
            </div>

            <div className="cQuickItem">
              <span className="cQuickIcon">
                <FiSend />
              </span>
              <div>
                <div className="cQuickTop">Order Support</div>
                <div className="cQuickBottom">Share your Order ID</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="cSection">
        <div className="cWrap">
          {/* ✅ LEFT COLUMN: FORM + PROMISE CARD */}
          <div className="cLeft">
            {/* FORM */}
            <div className="cCard cFormCard">
              <div className="cCardHead">
                <h2 className="cCardTitle">Send a message</h2>
                <p className="cCardSub">We’ll get back as soon as possible.</p>
              </div>

              <form className="cForm" onSubmit={onSubmit}>
                <div className="cRow">
                  <label className="cLabel">
                    Name
                    <input 
                      className="cInput" 
                      name="name"
                      placeholder="Your name" 
                      value={formData.name}
                      onChange={handleChange}
                      required 
                    />
                  </label>

                  <label className="cLabel">
                    Email
                    <input
                      className="cInput"
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </label>
                </div>

                <div className="cRow">
                  <label className="cLabel">
                    Mobile Number
                    <input
                      className="cInput"
                      type="tel"
                      name="mobile"
                      placeholder="+91 0000000000"
                      value={formData.mobile}
                      onChange={handleChange}
                      required
                    />
                  </label>

                  <label className="cLabel">
                    Order ID (optional)
                    <input 
                      className="cInput" 
                      name="orderId"
                      placeholder="AYR-12345" 
                      value={formData.orderId}
                      onChange={handleChange}
                    />
                  </label>
                </div>

                <label className="cLabel">
                  Topic
                  <select 
                    className="cInput" 
                    name="topic"
                    value={formData.topic}
                    onChange={handleChange}
                  >
                    <option value="order">Order / Delivery</option>
                    <option value="returns">Returns / Refunds</option>
                    <option value="product">Product / Stock</option>
                    <option value="other">Other</option>
                  </select>
                </label>

                <label className="cLabel">
                  Message
                  <textarea
                    className="cTextarea"
                    name="message"
                    rows="6"
                    placeholder="Write your message..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </label>

                <div className="cActions">
                  <button className="cBtn" type="submit">
                    <FiSend /> Send
                  </button>

                  {status === "sent" && (
                    <div className="cToast" role="status" aria-live="polite">
                      ✅ Message sent! We’ll reply soon.
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* ✅ MOVED HERE: AYRINE PROMISE CARD (below form on left) */}
            <div className="cCard cMiniBanner">
              <div className="cMiniTitle">Ayrine Promise</div>
              <div className="cMiniSub">
                Premium look, clean finish, and customer-first support — always.
              </div>
              <div className="cMiniBadges">
                <span className="cBadge">
                  <FiMail /> Email Support
                </span>
                <span className="cBadge">
                  <FiClock /> Fast Replies
                </span>
                <span className="cBadge">
                  <FiMapPin /> India-wide
                </span>
              </div>
            </div>
          </div>

          {/* ✅ RIGHT COLUMN: REACH US + FAQ */}
          <div className="cSide">
            <div className="cCard">
              <div className="cCardHead">
                <h2 className="cCardTitle">Reach us</h2>
                <p className="cCardSub">Support details for quick help.</p>
              </div>

              <div className="cInfoGrid">
                {info.map((i) => (
                  <div className="cInfo" key={i.title}>
                    <div className="cInfoIcon">{i.icon}</div>
                    <div className="cInfoText">
                      <div className="cInfoTitle">{i.title}</div>
                      <div className="cInfoValue">{i.value}</div>
                      <div className="cInfoSub">{i.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cNote">
                <div className="cNoteTitle">Tip</div>
                <div className="cNoteText">
                  For faster support, include your <b>Order ID</b> and the
                  product name.
                </div>
              </div>
            </div>

            <div className="cCard">
              <div className="cCardHead">
                <h2 className="cCardTitle">Quick FAQs</h2>
                <p className="cCardSub">Common questions we get.</p>
              </div>

              <div className="cFaq">
                {faqs.map((f) => (
                  <details className="cFaqItem" key={f.q}>
                    <summary className="cFaqQ">{f.q}</summary>
                    <div className="cFaqA">{f.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOT STRIP */}
      <section className="cStrip" aria-label="Contact highlight">
        <div className="cStripInner">
          <div className="cStripLeft">
            <div className="cStripTop">Need help choosing?</div>
            <div className="cStripBottom">
              Tell us your style and budget — we’ll suggest picks.
            </div>
          </div>

          <div className="cStripRight">
            <a className="cStripBtn" href="mailto:support@ayrine.in">
              <FiMail /> Email Us
            </a>
            <a className="cStripBtn ghost" href="tel:(+91) 9885881181">
              <FiPhone /> Call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}